import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  TextField,
  Typography,
  IconButton,
  Avatar,
  Alert,
  Snackbar,
  AlertTitle,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getCertificadoPlantillaById,
  postCertificadoPlantilla,
  putCertificadoPlantilla,
} from '../../modules/apps/user-management/users-list/core/_requests';
import { PageTitle } from '../../../_metronic/layout/core';
import BackButton from '../../components/BackButton/BackButton';

interface PlantillaForm {
  nombre_plantilla: string;
  entidad: string;
  texto_certifican: string;
  titulo_certificado: string;
  texto_certificado: string;
  texto_intensidad_horaria: string;
  texto_contexto: string;
  nombre_firmante: string;
  cargo_firmante: string;
  area_firmante: string;
  firma_base64: string; // Cambiado de firma_url a firma_base64
  logos_base64: string[]; // Cambiado de logos_urls a logos_base64
  fondo_base64: string; // Cambiado de fondo_url a fondo_base64
  color_primario: string;
  color_secundario: string;
  fuente_titulo: string;
  fuente_texto: string;
  tamano_fuente_titulo: number;
  tamano_fuente_texto: number;
  vigente: boolean;
}

const CertificadoPlantillaFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<PlantillaForm>({
    nombre_plantilla: '',
    entidad: 'SECRETARÍA DISTRITAL DE DESARROLLO ECONÓMICO DE BOGOTÁ Y LA AGENCIA DISTRITAL DE EMPLEO',
    texto_certifican: 'Certifican que:',
    titulo_certificado: 'CERTIFICADO DE FORMACIÓN',
    texto_certificado: 'El(la) señor(a) {{nombre}}\nCedula de ciudadanía {{documento}}\n\nCertifica la participación en el taller de habilidades blandas "{{curso}}"',
    texto_intensidad_horaria: 'Intensidad horaria: {{horas}} horas',
    texto_contexto: 'Esta capacitación se enmarca en la estrategia para fortalecer las competencias blandas de los buscadores de empleo adscritos a la Agencia Pública de Empleo del Distrito de Bogotá',
    nombre_firmante: '',
    cargo_firmante: '',
    area_firmante: '',
    firma_base64: '',
    logos_base64: [],
    fondo_base64: '',
    color_primario: '#003876',
    color_secundario: '#00A3E0',
    fuente_titulo: 'Arial',
    fuente_texto: 'Arial',
    tamano_fuente_titulo: 28,
    tamano_fuente_texto: 14,
    vigente: false,
  });

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);

  // Notificaciones
  const [open, setOpen] = useState(false);
  const [alert, setAlert] = useState(true);
  const [alertMsg, setAlertMsg] = useState('');

  useEffect(() => {
    if (isEditMode && id) {
      fetchPlantilla();
    }
  }, [id]);

  const showAlert = (success: boolean, message: string) => {
    setAlert(success);
    setAlertMsg(message);
    setOpen(true);
    setTimeout(() => {
      setOpen(false);
    }, 3000);
  };

  const fetchPlantilla = async () => {
    try {
      setFetching(true);
      const response = await getCertificadoPlantillaById(Number(id));
      
      let plantillaData = null;
      
      if (response && response.data && response.data.data) {
        plantillaData = response.data.data;
      } else if (response && response.data) {
        plantillaData = response.data;
      }
      
      if (plantillaData) {
        const mappedData: PlantillaForm = {
          nombre_plantilla: plantillaData.nombre_plantilla || '',
          entidad: plantillaData.entidad || '',
          texto_certifican: plantillaData.texto_certifican || 'Certifican que:',
          titulo_certificado: plantillaData.titulo_certificado || 'CERTIFICADO DE FORMACIÓN',
          texto_certificado: plantillaData.texto_certificado || '',
          texto_intensidad_horaria: plantillaData.texto_intensidad_horaria || '',
          texto_contexto: plantillaData.texto_contexto || '',
          nombre_firmante: plantillaData.nombre_firmante || '',
          cargo_firmante: plantillaData.cargo_firmante || '',
          area_firmante: plantillaData.area_firmante || '',
          firma_base64: plantillaData.firma_base64 || '',
          logos_base64: Array.isArray(plantillaData.logos_base64) ? plantillaData.logos_base64 : [],
          fondo_base64: plantillaData.fondo_base64 || '',
          color_primario: plantillaData.color_primario || '#003876',
          color_secundario: plantillaData.color_secundario || '#00A3E0',
          fuente_titulo: plantillaData.fuente_titulo || 'Arial',
          fuente_texto: plantillaData.fuente_texto || 'Arial',
          tamano_fuente_titulo: plantillaData.tamano_fuente_titulo || 28,
          tamano_fuente_texto: plantillaData.tamano_fuente_texto || 14,
          vigente: plantillaData.vigente || false,
        };
        
        setFormData(mappedData);
      }
    } catch (error: any) {
      console.error('Error fetching plantilla:', error);
      showAlert(false, 'Error al cargar la plantilla para edición');
    } finally {
      setFetching(false);
    }
  };

  const handleInputChange = (field: keyof PlantillaForm, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Función para convertir archivo a base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, field: 'firma_base64' | 'fondo_base64') => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showAlert(false, 'El archivo no debe superar 5MB');
      return;
    }

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      showAlert(false, 'Solo se permiten imágenes (JPEG, PNG, GIF, SVG)');
      return;
    }

    try {
      setUploading(field);
      const base64String = await fileToBase64(file);
      handleInputChange(field, base64String);
      showAlert(true, 'Imagen cargada exitosamente');
    } catch (error: any) {
      console.error('Error converting file to base64:', error);
      showAlert(false, 'Error al procesar la imagen');
    } finally {
      setUploading(null);
      event.target.value = '';
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showAlert(false, 'El archivo no debe superar 5MB');
      return;
    }

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      showAlert(false, 'Solo se permiten imágenes (JPEG, PNG, GIF, SVG)');
      return;
    }

    try {
      setUploading('logos');
      const base64String = await fileToBase64(file);
      const newLogos = [...formData.logos_base64, base64String];
      handleInputChange('logos_base64', newLogos);
      showAlert(true, 'Logo agregado exitosamente');
    } catch (error: any) {
      console.error('Error converting logo to base64:', error);
      showAlert(false, 'Error al procesar el logo');
    } finally {
      setUploading(null);
      event.target.value = '';
    }
  };

  const handleRemoveLogo = (index: number) => {
    const newLogos = formData.logos_base64.filter((_, i) => i !== index);
    handleInputChange('logos_base64', newLogos);
  };

  const handleRemoveFile = (field: 'firma_base64' | 'fondo_base64') => {
    handleInputChange(field, '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nombre_plantilla || !formData.nombre_plantilla.trim()) {
      showAlert(false, 'El nombre de la plantilla es obligatorio');
      return;
    }
    if (!formData.nombre_firmante || !formData.nombre_firmante.trim() || 
        !formData.cargo_firmante || !formData.cargo_firmante.trim()) {
      showAlert(false, 'Los datos del firmante son obligatorios');
      return;
    }

    try {
      setLoading(true);
      
      if (isEditMode && id) {
        await putCertificadoPlantilla(Number(id), formData);
        showAlert(true, 'Plantilla actualizada exitosamente');
      } else {
        await postCertificadoPlantilla(formData);
        showAlert(true, 'Plantilla creada exitosamente');
      }
      setTimeout(() => {
        navigate('/admin/certificado-plantilla');
      }, 1500);
    } catch (error: any) {
      console.error('Error saving plantilla:', error);
      showAlert(false, error.response?.data?.message || 'Error al guardar la plantilla');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (fetching) {
    return (
      <div className='p-4 shadow-4 rounded-3 mt-15' style={{ backgroundColor: '#FFFF' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <>
      <div className='p-4 shadow-4 rounded-3 mt-15' style={{ backgroundColor: '#FFFF' }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <BackButton />
          <Box>
            <Typography variant="h4">
              {isEditMode ? 'Editar Plantilla' : 'Nueva Plantilla'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configura los elementos del certificado de formación
            </Typography>
          </Box>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Configuración General */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Configuración General
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Nombre de la Plantilla *"
                        value={formData.nombre_plantilla || ''}
                        onChange={(e) => handleInputChange('nombre_plantilla', e.target.value)}
                        placeholder="Ej: Plantilla Habilidades Blandas 2024"
                        required
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Encabezado Institucional */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Encabezado Institucional
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Nombre de la Entidad Emisora"
                        value={formData.entidad || ''}
                        onChange={(e) => handleInputChange('entidad', e.target.value)}
                        placeholder="Ej: SECRETARÍA DISTRITAL DE DESARROLLO ECONÓMICO..."
                        helperText="Aparecerá en la parte superior del certificado"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Texto antes del nombre"
                        value={formData.texto_certifican || ''}
                        onChange={(e) => handleInputChange('texto_certifican', e.target.value)}
                        placeholder="Ej: Certifican que:"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Título del Certificado *"
                        value={formData.titulo_certificado || ''}
                        onChange={(e) => handleInputChange('titulo_certificado', e.target.value)}
                        required
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Texto del Certificado */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Contenido del Certificado
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    Variables disponibles: {'{'}
                    {'{'}nombre{'}'}
                    {'}'}, {'{'}
                    {'{'}documento{'}'}
                    {'}'}, {'{'}
                    {'{'}curso{'}'}
                    {'}'}, {'{'}
                    {'{'}horas{'}'}
                    {'}'}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Texto Principal del Certificado *"
                        value={formData.texto_certificado || ''}
                        onChange={(e) => handleInputChange('texto_certificado', e.target.value)}
                        helperText="Usa {{nombre}}, {{documento}}, {{curso}} como variables"
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Texto Intensidad Horaria"
                        value={formData.texto_intensidad_horaria || ''}
                        onChange={(e) => handleInputChange('texto_intensidad_horaria', e.target.value)}
                        helperText="Usa {{horas}} como variable"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Texto de Contexto del Programa"
                        value={formData.texto_contexto || ''}
                        onChange={(e) => handleInputChange('texto_contexto', e.target.value)}
                        helperText="Texto adicional que describe el marco del programa"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Firma y Datos del Firmante */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Firma y Datos del Firmante
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Nombre del Firmante *"
                        value={formData.nombre_firmante || ''}
                        onChange={(e) => handleInputChange('nombre_firmante', e.target.value)}
                        placeholder="Ej: Guillermo Alarcón Plata"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Cargo *"
                        value={formData.cargo_firmante || ''}
                        onChange={(e) => handleInputChange('cargo_firmante', e.target.value)}
                        placeholder="Ej: Director"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Área"
                        value={formData.area_firmante || ''}
                        onChange={(e) => handleInputChange('area_firmante', e.target.value)}
                        placeholder="Ej: Dirección de Desarrollo Empresarial y Empleo"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Imagen de Firma
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                          <Button
                            variant="outlined"
                            component="label"
                            startIcon={uploading === 'firma_base64' ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                            disabled={uploading === 'firma_base64'}
                          >
                            {uploading === 'firma_base64' ? 'Cargando...' : 'Cargar Firma'}
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={(e) => handleFileUpload(e, 'firma_base64')}
                            />
                          </Button>
                          {formData.firma_base64 && formData.firma_base64.trim() !== '' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar
                                src={formData.firma_base64}
                                variant="square"
                                sx={{ width: 100, height: 50 }}
                              />
                              <Typography variant="caption" sx={{ maxWidth: 150, wordBreak: 'break-all' }}>
                                Firma cargada
                              </Typography>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveFile('firma_base64')}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Logos Institucionales (Footer) */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Logos Institucionales (Footer)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    Los logos aparecerán al final del certificado en línea horizontal centrada
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={uploading === 'logos' ? <CircularProgress size={20} /> : <AddIcon />}
                    disabled={uploading === 'logos'}
                    sx={{ mb: 2 }}
                  >
                    {uploading === 'logos' ? 'Cargando...' : 'Agregar Logo'}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleLogoUpload}
                    />
                  </Button>

                  {formData.logos_base64 && formData.logos_base64.length > 0 && (
                    <List>
                      {formData.logos_base64.map((logoBase64, index) => (
                        <ListItem key={index}>
                          <ListItemAvatar>
                            <Avatar
                              src={logoBase64}
                              variant="square"
                              sx={{ width: 60, height: 60 }}
                            />
                          </ListItemAvatar>
                          <ListItemText
                            primary={`Logo ${index + 1}`}
                            secondary={`Logo cargado ${index + 1}`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              color="error"
                              onClick={() => handleRemoveLogo(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Fondo del Certificado */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Fondo del Certificado
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={uploading === 'fondo_base64' ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                      disabled={uploading === 'fondo_base64'}
                    >
                      {uploading === 'fondo_base64' ? 'Cargando...' : 'Cargar Imagen de Fondo'}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, 'fondo_base64')}
                      />
                    </Button>
                    {formData.fondo_base64 && formData.fondo_base64.trim() !== '' && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={formData.fondo_base64}
                          variant="square"
                          sx={{ width: 80, height: 60 }}
                        />
                        <Typography variant="caption" sx={{ maxWidth: 150, wordBreak: 'break-all' }}>
                          Fondo cargado
                        </Typography>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveFile('fondo_base64')}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Estilos y Colores */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Estilos y Colores
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        type="color"
                        label="Color Primario"
                        value={formData.color_primario || '#003876'}
                        onChange={(e) => handleInputChange('color_primario', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        type="color"
                        label="Color Secundario"
                        value={formData.color_secundario || '#00A3E0'}
                        onChange={(e) => handleInputChange('color_secundario', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Tamaño Fuente Título"
                        value={formData.tamano_fuente_titulo || 28}
                        onChange={(e) =>
                          handleInputChange('tamano_fuente_titulo', Number(e.target.value))
                        }
                        InputProps={{ inputProps: { min: 12, max: 48 } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Tamaño Fuente Texto"
                        value={formData.tamano_fuente_texto || 14}
                        onChange={(e) =>
                          handleInputChange('tamano_fuente_texto', Number(e.target.value))
                        }
                        InputProps={{ inputProps: { min: 10, max: 24 } }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Botones de Acción */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/certificado-plantilla')}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button type="submit" variant="contained" color="primary" disabled={loading}>
                  {loading ? (
                    <>
                      <CircularProgress size={20} style={{ marginRight: 8 }} />
                      Guardando...
                    </>
                  ) : (
                    isEditMode ? 'Actualizar' : 'Crear Plantilla'
                  )}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </div>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={open}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          variant={'filled'}
          severity={alert ? 'success' : 'error'}
          sx={{ whiteSpace: 'pre-line' }}
        >
          {alert ? <AlertTitle>Éxito</AlertTitle> : <AlertTitle>Error</AlertTitle>}
          {alertMsg}
        </Alert>
      </Snackbar>
    </>
  );
};

const AdminCertificadoPlantillaFormWrapper = () => {
  return (
    <>
      <PageTitle breadcrumbs={[]}>Formulario de Plantilla</PageTitle>
      <CertificadoPlantillaFormPage />
    </>
  );
};

export { AdminCertificadoPlantillaFormWrapper };
export default AdminCertificadoPlantillaFormWrapper;